import dayjs from "dayjs";
import type { ProductItemIntegrationResponse } from "apiClients/base-generated";
import type { IntegrationRowLockEntry, IntegrationRowLocks } from "./integrationRowLockTypes";

/** Bygger låspost från API-fält (tooltip / ikon när `rowLocks` saknar override). */
export function lockEntryFromProductItemIntegrationRecord(
  record: ProductItemIntegrationResponse,
): IntegrationRowLockEntry | null {
  const until = record.lockedUntil;
  if (until == null || until === "") return null;
  return {
    lockedUntil: until,
    pausedStockQuantity: record.northmillLockedQuantity ?? null,
  };
}

/**
 * Lokal `rowLocks[key]` om satt; annars API (`lockedUntil` / `northmillLockedQuantity`).
 * `undefined` på nyckel = använd server; `null` = uttryckligen olåst i UI tills refetch.
 */
export function effectiveLockEntryForRow(
  rowKey: string,
  rowLocks: IntegrationRowLocks | undefined,
  record: ProductItemIntegrationResponse,
): IntegrationRowLockEntry | string | null {
  const local = rowLocks?.[rowKey];
  if (local !== undefined) {
    return local;
  }
  return lockEntryFromProductItemIntegrationRecord(record);
}

function getLockedUntilIso(
  entry: IntegrationRowLockEntry | string | null | undefined,
): string | null {
  if (entry == null || entry === "") return null;
  if (typeof entry === "string") return entry;
  return entry.lockedUntil;
}

export function getPausedStockQuantity(
  entry: IntegrationRowLockEntry | string | null | undefined,
): number | null {
  if (entry == null || typeof entry === "string") return null;
  return entry.pausedStockQuantity ?? null;
}

/** `lockedUntil` is ISO 8601; lock gäller t.o.m. slutet av det valda datumet. */
export function isRowLockActive(entry: IntegrationRowLockEntry | string | null | undefined): boolean {
  const until = getLockedUntilIso(entry);
  if (until == null || until === "") return false;
  return dayjs(until).isAfter(dayjs());
}

export function formatLockUntilLabel(
  entry: IntegrationRowLockEntry | string | null | undefined,
): string | null {
  const until = getLockedUntilIso(entry);
  if (!isRowLockActive(entry) || until == null) return null;
  return dayjs(until).format("YYYY-MM-DD");
}

export function formatPausedStockLabel(
  entry: IntegrationRowLockEntry | string | null | undefined,
): string | null {
  const qty = getPausedStockQuantity(entry);
  if (qty == null) return null;
  return `${qty} st`;
}
